import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Card,
    CardContent,
    Tooltip,
    Divider,
    IconButton,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import {
    useInquiries,
    useInquirySummary,
    useInquiryDetail,
    useAnswerInquiry,
    useChangeInquiryStatus,
} from '../../hooks/useInquiry';
import type { InquiryItem, InquiryStatus, InquiryCategory } from '../../types/inquiry';
import { STATUS_LABELS, STATUS_COLORS, CATEGORY_LABELS } from '../../types/inquiry';
import { formatKstDateTime } from '../../utils/dateTime';

const formatDate = (dateStr?: string) => {
    return formatKstDateTime(dateStr);
};

export const InquiryList: FC = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState<InquiryStatus | ''>('');
    const [categoryFilter, setCategoryFilter] = useState<InquiryCategory | ''>('');
    const [keyword, setKeyword] = useState('');
    const [keywordInput, setKeywordInput] = useState('');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [answerText, setAnswerText] = useState('');

    const { data, isLoading } = useInquiries({
        page,
        size,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(categoryFilter ? { category: categoryFilter } : {}),
        ...(keyword ? { keyword } : {}),
    });
    const { data: summary } = useInquirySummary();
    const { data: detail, isLoading: detailLoading } = useInquiryDetail(selectedId);
    const answerInquiry = useAnswerInquiry();
    const changeStatus = useChangeInquiryStatus();

    const inquiries = data?.content ?? [];
    const totalCount = data?.totalCount ?? 0;

    const handleOpenDetail = (inquiry: InquiryItem) => {
        setSelectedId(inquiry.id);
        setAnswerText('');
    };

    const handleClose = () => {
        setSelectedId(null);
        setAnswerText('');
    };

    const handleAnswer = () => {
        if (!selectedId || !answerText.trim()) return;
        answerInquiry.mutate(
            { id: selectedId, answer: answerText.trim() },
            { onSuccess: () => setAnswerText('') },
        );
    };

    const handleChangeStatus = (status: string) => {
        if (!selectedId) return;
        changeStatus.mutate({ id: selectedId, status }, { onSuccess: handleClose });
    };

    const handleSearch = () => {
        setKeyword(keywordInput.trim());
        setPage(1);
    };

    const handleClearSearch = () => {
        setKeywordInput('');
        setKeyword('');
        setPage(1);
    };

    return (
        <Box p={3}>
            <Typography variant="h4" sx={{ mb: 3 }}>고객문의 관리</Typography>

            {/* Summary Cards */}
            {summary && (
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <SummaryCard label="답변 대기" count={summary.pendingCount} color="#f59e0b" />
                    <SummaryCard label="답변 작성중" count={summary.inProgressCount} color="#3b82f6" />
                    <SummaryCard label="답변 완료" count={summary.answeredCount} color="#22c55e" />
                    <SummaryCard label="종료" count={summary.closedCount} color="#94a3b8" />
                </Stack>
            )}

            {/* Filters */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>상태</InputLabel>
                    <Select
                        value={statusFilter}
                        label="상태"
                        onChange={(e) => { setStatusFilter(e.target.value as InquiryStatus | ''); setPage(1); }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {(Object.keys(STATUS_LABELS) as InquiryStatus[]).map((s) => (
                            <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>카테고리</InputLabel>
                    <Select
                        value={categoryFilter}
                        label="카테고리"
                        onChange={(e) => { setCategoryFilter(e.target.value as InquiryCategory | ''); setPage(1); }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {(Object.keys(CATEGORY_LABELS) as InquiryCategory[]).map((c) => (
                            <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    placeholder="제목/내용 검색"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    sx={{ minWidth: 200 }}
                />
                <Button variant="outlined" size="small" onClick={handleSearch}>검색</Button>
                {keyword && (
                    <Button size="small" onClick={handleClearSearch}>초기화</Button>
                )}
            </Stack>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>카테고리</TableCell>
                            <TableCell>제목</TableCell>
                            <TableCell>사용자</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell>답변</TableCell>
                            <TableCell>접수일</TableCell>
                            <TableCell align="center">보기</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>로딩 중...</TableCell>
                            </TableRow>
                        ) : inquiries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    문의 내역이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            inquiries.map((inquiry) => (
                                <TableRow key={inquiry.id} hover>
                                    <TableCell>{inquiry.id}</TableCell>
                                    <TableCell>
                                        <Chip label={inquiry.categoryDescription} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={inquiry.title}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                                {inquiry.title}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                            {inquiry.userNickname ?? inquiry.userEmail ?? `#${inquiry.userId}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={STATUS_LABELS[inquiry.status] ?? inquiry.status}
                                            color={STATUS_COLORS[inquiry.status] ?? 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {inquiry.hasAnswer ? (
                                            <Chip label="완료" color="success" size="small" variant="outlined" />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{formatDate(inquiry.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenDetail(inquiry)}>
                                            <ViewIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(1); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="페이지당"
                />
            </TableContainer>

            {/* Detail Dialog */}
            <Dialog open={selectedId != null} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    고객문의 상세
                    {detail && (
                        <Chip
                            label={STATUS_LABELS[detail.status] ?? detail.status}
                            color={STATUS_COLORS[detail.status] ?? 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    {detailLoading || !detail ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>불러오는 중...</Typography>
                    ) : (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            {/* 문의 메타 정보 */}
                            <Stack direction="row" spacing={3}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">카테고리</Typography>
                                    <Typography variant="body2" fontWeight={600}>{detail.categoryDescription}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">사용자</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {detail.userNickname ?? detail.userEmail ?? `#${detail.userId}`}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">접수일</Typography>
                                    <Typography variant="body2">{formatDate(detail.createdAt)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">앱 버전</Typography>
                                    <Typography variant="body2" fontWeight={600}>{detail.appVersion ?? '-'}</Typography>
                                </Box>
                            </Stack>

                            {/* 제목 */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">제목</Typography>
                                <Typography variant="body1" fontWeight={600}>{detail.title}</Typography>
                            </Box>

                            {/* 문의 내용 */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">문의 내용</Typography>
                                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#f8fafc' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {detail.content}
                                    </Typography>
                                </Paper>
                            </Box>

                            <Divider />

                            {/* 기존 답변 */}
                            {detail.answer && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        답변 ({formatDate(detail.answeredAt)})
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#f0fdf4' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {detail.answer}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}

                            {/* 답변 작성 (CLOSED 아닌 경우) */}
                            {detail.status !== 'CLOSED' && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {detail.answer ? '답변 수정' : '답변 작성'}
                                    </Typography>
                                    <TextField
                                        multiline
                                        rows={5}
                                        fullWidth
                                        placeholder="답변 내용을 입력하세요..."
                                        value={answerText}
                                        onChange={(e) => setAnswerText(e.target.value)}
                                        sx={{ mt: 0.5 }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                    {/* 좌측: 상태 변경 버튼 */}
                    <Stack direction="row" spacing={1}>
                        {detail?.status === 'PENDING' && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleChangeStatus('IN_PROGRESS')}
                                disabled={changeStatus.isPending}
                            >
                                답변 작성중으로 변경
                            </Button>
                        )}
                        {detail?.status !== 'CLOSED' && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleChangeStatus('CLOSED')}
                                disabled={changeStatus.isPending}
                            >
                                종료
                            </Button>
                        )}
                    </Stack>
                    {/* 우측: 답변 저장 + 닫기 */}
                    <Stack direction="row" spacing={1}>
                        <Button onClick={handleClose}>닫기</Button>
                        {detail?.status !== 'CLOSED' && (
                            <Button
                                variant="contained"
                                onClick={handleAnswer}
                                disabled={!answerText.trim() || answerInquiry.isPending}
                            >
                                답변 저장
                            </Button>
                        )}
                    </Stack>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const SummaryCard: FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
    <Card sx={{ flex: 1, minWidth: 120 }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color }}>{count}</Typography>
        </CardContent>
    </Card>
);
